import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Radar, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError, MONTHS, STAGE_LABELS } from "../adminApi";
import { ScoreBar, inputClass } from "../components/Bits";

const PAGE_SIZE = 25;

/** Incumbent PEO cell: a name when we have one, otherwise the strength of the signal. */
const PeoCell = ({ account }) => {
  const status = account.peo_status || "unknown";
  if (account.current_peo) {
    return (
      <span className="text-xs text-gold" title={account.peo_signal?.method || ""}>
        {account.current_peo}
      </span>
    );
  }
  if (status === "likely") {
    return (
      <span className="text-xs text-sage" title={account.peo_signal?.method || ""}>
        Likely — name unknown
      </span>
    );
  }
  if (status === "peo_entity") {
    return <span className="text-xs text-terra">Is a PEO</span>;
  }
  return <span className="text-xs text-cream/30">—</span>;
};

const emptyFilters = {
  search: "",
  segment: "",
  stage: "",
  city: "",
  industry: "",
  renewal_month: "",
  peo_status: "",
  min_score: "",
  min_participants: "",
  max_participants: "",
};

const PHASE_LABELS = {
  form5500: "Pulling the employer filings",
  schedule_a: "Attaching carrier and broker detail",
  filing_contacts: "Naming the people who signed them",
};

/** One button that rebuilds the Accounts database from the latest DOL filings.
 *  Campaigns, sequences, deals and scheduling are untouched. */
const RefreshEmployerData = ({ onDone }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/crm/accounts/refresh/status");
      setStatus(data);
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(async () => {
      const next = await load();
      if (next && !next.running) onDone();
    }, 6000);
    return () => clearInterval(timer);
  }, [load, onDone]);

  const start = async () => {
    setBusy(true);
    try {
      await adminApi.post("/crm/accounts/refresh", { year: 2024, refresh_download: true });
      toast.success("Employer refresh started — it runs in the background, this page keeps working");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const running = Boolean(status?.running);
  const last = status?.phases?.[status.phases.length - 1]?.run;
  const failed = status?.phases?.find((phase) => phase.run?.status === "failed");

  return (
    <div className="text-right" data-testid="accounts-refresh">
      <button
        type="button"
        onClick={start}
        disabled={busy || running}
        data-testid="accounts-refresh-button"
        className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-5 py-2 text-xs font-medium text-gold transition-colors duration-200 hover:bg-gold/20 disabled:opacity-50"
      >
        {busy || running ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                         : <RefreshCw className="h-3.5 w-3.5" />}
        {running ? "Refreshing…" : "Refresh employer data"}
      </button>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-cream/35"
         data-testid="accounts-refresh-status">
        {running
          ? PHASE_LABELS[status.current_phase] || "Working"
          : failed
            ? `Last run failed — ${failed.run.error?.slice(0, 60) || "see Data sources"}`
            : last?.started_at
              ? `Last refreshed ${new Date(last.started_at).toLocaleDateString()} · ${(status.accounts || 0).toLocaleString()} employers`
              : "Never refreshed on this site"}
      </p>
    </div>
  );
};

export default function AdminAccounts() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(emptyFilters);
  const [options, setOptions] = useState({ cities: [], industries: [], stages: [], segments: [] });
  const [data, setData] = useState({ accounts: [], total: 0 });
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ field: "score", direction: "desc" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    adminApi.get("/crm/filters").then(({ data: d }) => setOptions(d)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, sort: sort.field, direction: sort.direction };
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "") params[key] = value;
      });
      const { data: d } = await adminApi.get("/crm/accounts", { params });
      setData(d);
      setError("");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters, page, sort]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const set = (key) => (event) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const toggleSort = (field) => {
    setPage(1);
    setSort((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === "desc" ? "asc" : "desc" }
        : { field, direction: "desc" }
    );
  };

  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="admin-accounts-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">Accounts</p>
          <h1 className="mt-3 font-serif text-4xl font-light leading-none text-cream">
            Target accounts
          </h1>
        </div>
        <div className="flex flex-wrap items-end gap-6">
          <p className="font-mono text-xs text-cream/45" data-testid="accounts-total">
            {data.total.toLocaleString()} matching
          </p>
          <RefreshEmployerData onDone={load} />
        </div>
      </div>

      <div className="grid gap-3 rounded-xl border border-cream/10 bg-[#10211A] p-4 sm:grid-cols-2 lg:grid-cols-4"
           data-testid="accounts-filters">
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-cream/30" />
          <input
            value={filters.search}
            onChange={set("search")}
            placeholder="Name, city or EIN"
            data-testid="accounts-search-input"
            className={`${inputClass} pl-9`}
          />
        </div>

        <select value={filters.segment} onChange={set("segment")} className={inputClass}
                data-testid="accounts-segment-select">
          <option value="">All segments</option>
          {options.segments.map((segment) => (
            <option key={segment} value={segment}>{segment}</option>
          ))}
        </select>

        <select value={filters.stage} onChange={set("stage")} className={inputClass}
                data-testid="accounts-stage-select">
          <option value="">All stages</option>
          {options.stages.map((stage) => (
            <option key={stage} value={stage}>{STAGE_LABELS[stage]}</option>
          ))}
        </select>

        <select value={filters.city} onChange={set("city")} className={inputClass}
                data-testid="accounts-city-select">
          <option value="">All cities</option>
          {options.cities.map((city) => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>

        <select value={filters.industry} onChange={set("industry")} className={inputClass}
                data-testid="accounts-industry-select">
          <option value="">All industries</option>
          {options.industries.map((industry) => (
            <option key={industry} value={industry}>{industry}</option>
          ))}
        </select>

        <select value={filters.renewal_month} onChange={set("renewal_month")} className={inputClass}
                data-testid="accounts-renewal-select">
          <option value="">Any renewal month</option>
          {MONTHS.map((month, index) => (
            <option key={month} value={index + 1}>Renews {month}</option>
          ))}
        </select>

        <select value={filters.peo_status} onChange={set("peo_status")} className={inputClass}
                data-testid="accounts-peo-select">
          <option value="">Any PEO status</option>
          {(options.peo_statuses || []).map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        <input value={filters.min_score} onChange={set("min_score")} type="number" min="0" max="100"
               placeholder="Min score" className={inputClass} data-testid="accounts-minscore-input" />

        <div className="flex gap-2">
          <input value={filters.min_participants} onChange={set("min_participants")} type="number" min="0"
                 placeholder="Min lives" className={inputClass} data-testid="accounts-minlives-input" />
          <input value={filters.max_participants} onChange={set("max_participants")} type="number" min="0"
                 placeholder="Max lives" className={inputClass} data-testid="accounts-maxlives-input" />
        </div>
      </div>

      {error ? <p className="text-sm text-terra" data-testid="accounts-error">{error}</p> : null}

      <div className="overflow-x-auto rounded-xl border border-cream/10">
        <table className="w-full min-w-[820px] border-collapse text-left">
          <thead>
            <tr className="bg-[#10211A] font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
              {[
                ["name", "Account"],
                ["participants", "Lives"],
                ["renewal_month", "Renews"],
                ["score", "Fit"],
              ].map(([field, label]) => (
                <th key={field} className="px-4 py-3">
                  <button type="button" onClick={() => toggleSort(field)}
                          data-testid={`sort-${field}`}
                          className="uppercase tracking-[0.2em] transition-colors duration-200 hover:text-cream">
                    {label}
                    {sort.field === field ? (sort.direction === "desc" ? " ↓" : " ↑") : ""}
                  </button>
                </th>
              ))}
              <th className="px-4 py-3">Incumbent PEO</th>
              <th className="px-4 py-3">Segment</th>
              <th className="px-4 py-3">Stage</th>
              <th className="px-4 py-3">Radar</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cream/10 bg-[#0E1A13]">
            {loading && data.accounts.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-cream/40">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                </td>
              </tr>
            ) : null}

            {data.accounts.map((account) => (
              <tr key={account.id} className="transition-colors duration-200 hover:bg-cream/5"
                  data-testid={`account-row-${account.id}`}
                  onClick={() => navigate(`/admin/accounts/${account.id}`)}
                  style={{ cursor: "pointer" }}>
                <td className="px-4 py-3">
                  <Link to={`/admin/accounts/${account.id}`} onClick={(e) => e.stopPropagation()}
                        className="text-sm text-cream transition-colors duration-200 hover:text-gold">
                    {account.name}
                  </Link>
                  <p className="mt-0.5 text-xs text-cream/40">
                    {[account.city, account.industry].filter(Boolean).join(" · ") || "—"}
                  </p>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-cream/70">{account.participants}</td>
                <td className="px-4 py-3 text-xs text-cream/60">
                  {account.renewal_month ? MONTHS[account.renewal_month - 1] : "—"}
                </td>
                <td className="px-4 py-3"><ScoreBar score={account.score || 0} /></td>
                <td className="px-4 py-3" data-testid={`account-peo-${account.id}`}>
                  <PeoCell account={account} />
                </td>
                <td className="px-4 py-3 text-xs text-cream/60">{account.segment}</td>
                <td className="px-4 py-3 text-xs text-cream/60">
                  {STAGE_LABELS[account.stage] || "New"}
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    title="Run the Co-Employer Radar on this account"
                    data-testid={`account-radar-${account.id}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/admin/co-employer?company=${encodeURIComponent(account.name)}` +
                        `&state=${account.state || "TX"}&run=1`);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-cream/15 px-3 py-1.5 text-[11px] text-cream/60 transition-colors duration-200 hover:border-gold/50 hover:text-gold"
                  >
                    <Radar className="h-3 w-3" /> Run
                  </button>
                </td>
              </tr>
            ))}

            {!loading && data.accounts.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-cream/40"
                    data-testid="accounts-empty">
                  No accounts match these filters.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-4">
        <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                data-testid="accounts-prev-page"
                className="rounded-full border border-cream/15 px-4 py-2 text-xs text-cream/70 transition-colors duration-200 hover:border-gold/50 hover:text-cream disabled:opacity-30">
          Previous
        </button>
        <p className="font-mono text-xs text-cream/45" data-testid="accounts-page-label">
          Page {page} of {pages}
        </p>
        <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
                data-testid="accounts-next-page"
                className="rounded-full border border-cream/15 px-4 py-2 text-xs text-cream/70 transition-colors duration-200 hover:border-gold/50 hover:text-cream disabled:opacity-30">
          Next
        </button>
      </div>
    </div>
  );
}
